import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { SensitiveDataProtection } from '@/components/common/ProtectedComponent';
import { formatNZD } from '@/utils/currency';

interface RevenueData {
  date: string;
  revenue: number;
  bookings: number;
}

interface RevenueChartProps {
  data?: RevenueData[];
  loading?: boolean;
}

function buildEmptyTrendData(): RevenueData[] {
  const points: RevenueData[] = [];
  const now = new Date();

  for (let i = 6; i >= 0; i -= 1) {
    const date = new Date(now);
    date.setDate(now.getDate() - i);
    const label = `${date.getMonth() + 1}/${date.getDate()}`;
    points.push({ date: label, revenue: 0, bookings: 0 });
  }

  return points;
}

const RevenueChart: React.FC<RevenueChartProps> = ({
  data = [],
  loading = false
}) => {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="card card-comfortable">
        <div className="flex items-center justify-between mb-6">
          <div className="h-6 w-32 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="h-80 bg-gray-100 rounded-lg animate-pulse"></div>
      </div>
    );
  }

  // 自定义Tooltip
  type TooltipPayload = {
    color: string;
    dataKey: string;
    value: number;
  };

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: TooltipPayload[];
    label?: string;
  }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded-xl shadow-medium border border-gray-200">
          <p className="text-sm font-medium text-gray-900 mb-2">{`${t('bookings.date')}: ${label}`}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }}
              ></div>
              <span className="text-gray-600">
                {entry.dataKey === 'revenue' ? t('analytics.revenue') : t('analytics.bookings')}:
              </span>
              <span className="font-medium text-gray-900">
                {entry.dataKey === 'revenue' 
                  ? formatNZD(entry.value) 
                  : entry.value
                }
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (!data.length) {
    const emptyData = buildEmptyTrendData();

    return (
      <div className="card card-comfortable">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.revenueChart')}</h3>
            <p className="text-sm text-gray-500 mt-1">{t('dashboard.revenueChartDescription')}</p>
          </div>
        </div>

        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-center text-sm text-gray-500">
          {t('dashboard.noData')}
        </div>

        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={emptyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f1f5f9"
                vertical={false}
              />

              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#64748b' }}
                dy={10}
              />

              <YAxis
                yAxisId="revenue"
                orientation="left"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickFormatter={(value) => formatNZD(value, { maximumFractionDigits: 0 })}
              />

              <YAxis
                yAxisId="bookings"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#64748b' }}
              />

              <Area
                yAxisId="revenue"
                type="monotone"
                dataKey="revenue"
                stroke="#0ea5e9"
                strokeWidth={2}
                fill="#e0f2fe"
                fillOpacity={0.35}
                dot={{ fill: '#0ea5e9', strokeWidth: 0, r: 3 }}
              />

              <Line
                yAxisId="bookings"
                type="monotone"
                dataKey="bookings"
                stroke="#22c55e"
                strokeWidth={2}
                dot={{ fill: '#22c55e', strokeWidth: 0, r: 3 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  return (
    <SensitiveDataProtection 
      level="financial"
      placeholder={
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="card card-comfortable"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.revenueChart')}</h3>
              <p className="text-sm text-gray-500 mt-1">{t('settings.accessRestricted')}</p>
            </div>
          </div>
          <div className="h-80 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center text-gray-500">
              <div className="text-2xl mb-2">🔒</div>
              <p className="text-sm">{t('auth.permissionDenied')}</p>
              <p className="text-xs mt-1">{t('auth.helpText')}</p>
            </div>
          </div>
        </motion.div>
      }
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="card card-comfortable"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.revenueChart')}</h3>
            <p className="text-sm text-gray-500 mt-1">{t('dashboard.revenueChartDescription')}</p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-primary-500"></div>
              <span className="text-sm text-gray-600">{t('analytics.revenue')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-success-500"></div>
              <span className="text-sm text-gray-600">{t('analytics.bookings')}</span>
            </div>
          </div>
        </div>

        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.05}/>
                </linearGradient>
                <linearGradient id="bookingsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#f1f5f9"
                vertical={false}
              />
              
              <XAxis 
                dataKey="date" 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#64748b' }}
                dy={10}
              />
              
              <YAxis 
                yAxisId="revenue"
                orientation="left"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickFormatter={(value) => formatNZD(value, { maximumFractionDigits: 0 })}
              />
              
              <YAxis 
                yAxisId="bookings"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#64748b' }}
              />
              
              <Tooltip content={<CustomTooltip />} />
              
              <Area
                yAxisId="revenue"
                type="monotone"
                dataKey="revenue"
                stroke="#0ea5e9"
                strokeWidth={3}
                fill="url(#revenueGradient)"
                dot={{ fill: '#0ea5e9', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: '#0ea5e9', strokeWidth: 0 }}
              />
              
              <Line
                yAxisId="bookings"
                type="monotone"
                dataKey="bookings"
                stroke="#22c55e"
                strokeWidth={3}
                dot={{ fill: '#22c55e', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: '#22c55e', strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.div>
    </SensitiveDataProtection>
  );
};

export default RevenueChart;
